import {Vue} from 'vue-property-decorator';
import {AmetrasAdvancedButtonEvent} from '@/fields/button/AmetrasAdvancedButtonTemplate.interface';
import {Subject, Subscription} from 'rxjs';
import {
    AmetrasDisabledReasons,
    AmetrasHiddenReasons
} from '@/fields/advanced-field-function-executor/field-state-executor';
import {get, isEqual} from 'lodash';

export interface AmetrasAdvancedButtonState {
    disabledReasons: AmetrasDisabledReasons;
    hiddenReasons: AmetrasHiddenReasons;
    href: string | undefined;
    text: string | undefined;
    icon: DisguiseDto.DisguiseIcon | undefined;
}

type DataTypeBasedMapping = Partial<Record<DisguiseDto.WatchOptionEnum, {
    nativeEventName: string;
    cb: (arg: any) => void;
}>>;

export class ButtonStateExecutor {

    private readonly buttonState: AmetrasAdvancedButtonState;
    private readonly buttonRef: Vue;
    private readonly notifyFieldWatchOptionTriggeredFunction: () => Promise<unknown>;
    private readonly fieldReferenceDescription: DisguiseDto.AdvancedButtonReferenceDescription;
    private readonly allMyRegisteredListeners: Array<{
        eventName: string,
        callback: any
    }> = [];
    private buttonEventSubscription?: Subscription;

    private nativeEventNames: DataTypeBasedMapping = {
        BLUR: {
            nativeEventName: 'blur',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        CLICK: {
            nativeEventName: 'click',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        DOUBLECLICK: {
            nativeEventName: 'dblclick',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        FOCUS: {
            nativeEventName: 'focus',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        }
    };

    constructor(
        buttonRef: Vue,
        fieldReferenceDescription: DisguiseDto.AdvancedButtonReferenceDescription,
        buttonState: AmetrasAdvancedButtonState,
        notifyFieldWatchOptionTriggeredFunction: () => Promise<unknown>,
        onButtonEvent?: Subject<{ eventName: string, eventArgs: any }>
    ) {
        this.buttonRef = buttonRef;
        this.buttonState = buttonState;
        this.notifyFieldWatchOptionTriggeredFunction = notifyFieldWatchOptionTriggeredFunction;
        this.fieldReferenceDescription = fieldReferenceDescription;

        const fastFieldWatchMap: { [eventName: string]: (arg: any) => void } = {};
        for (const currentWatchOption of fieldReferenceDescription.watchOptions || []) {
            if (currentWatchOption.watchOption !== undefined) {
                const nativeMapping = this.nativeEventNames[currentWatchOption.watchOption];
                if (nativeMapping) {
                    fastFieldWatchMap[nativeMapping.nativeEventName] = nativeMapping.cb;
                }
            }
        }
        if (onButtonEvent !== undefined) {
            this.buttonEventSubscription = onButtonEvent.subscribe((event) => {
                const registeredWatchFunction = fastFieldWatchMap[event.eventName];
                if (registeredWatchFunction !== undefined) {
                    registeredWatchFunction(event.eventArgs);
                }
            });
        }

        for (const currentWatchOption of fieldReferenceDescription.watchOptions || []) {
            switch (currentWatchOption.watchOption) {
                case 'BLUR': {
                    const callback = () => {
                        this.onFieldEventTriggered();
                    };
                    const eventName = 'blur';
                    buttonRef.$on(eventName, callback);
                    this.allMyRegisteredListeners.push({
                        eventName,
                        callback
                    });
                    break;
                }
                case 'CLICK': {
                    const callback = (eventData: AmetrasAdvancedButtonEvent) => {
                        this.onFieldEventTriggered(eventData);
                    };
                    const eventName = 'click';
                    buttonRef.$on(eventName, callback);
                    this.allMyRegisteredListeners.push({
                        eventName,
                        callback
                    });
                    break;
                }
                case 'DOUBLECLICK': {
                    const callback = () => {
                        this.onFieldEventTriggered();
                    };
                    const eventName = 'dblclick';
                    buttonRef.$on(eventName, callback);
                    this.allMyRegisteredListeners.push({
                        eventName,
                        callback
                    });
                    break;
                }
                case 'FOCUS': {
                    const callback = () => {
                        this.onFieldEventTriggered();
                    };
                    const eventName = 'focus';
                    buttonRef.$on(eventName, callback);
                    this.allMyRegisteredListeners.push({
                        eventName,
                        callback
                    });
                    break;
                }
                default:
                    break;
            }
        }

    }

    afterInit() {
        const fieldWatchOptions = this.fieldReferenceDescription.watchOptions;
        for (const currentFieldWatchOption of fieldWatchOptions || []) {
            if (currentFieldWatchOption && currentFieldWatchOption.watchOption === 'INIT') {
                this.notifyFieldWatchOptionTriggeredFunction();
                break;
            }
        }
    }

    afterReInit() {
        const fieldWatchOptions = this.fieldReferenceDescription.watchOptions;
        for (const currentFieldWatchOption of fieldWatchOptions || []) {
            if (currentFieldWatchOption && currentFieldWatchOption.watchOption === 'REINIT') {
                this.notifyFieldWatchOptionTriggeredFunction();
                break;
            }
        }
    }

    /**
     * unbinds all listeners
     */
    destroy() {
        for (const currentRegisteredListener of this.allMyRegisteredListeners) {
            // https://vuejs.org/v2/api/#vm-off
            this.buttonRef.$off(currentRegisteredListener.eventName, currentRegisteredListener.callback);
        }
        if (this.buttonEventSubscription !== undefined) {
            this.buttonEventSubscription.unsubscribe();
        }
    }

    /**
     * After a new response has been collected
     *
     * We need to check whether we are affected
     */
    handleResponse(data: any) {
        let hiddenStatesChanged = false;
        if (this.fieldReferenceDescription.hiddenConditions) {
            let fieldHasHiddenConditionMatched: boolean = false;
            for (const hiddenCondition of this.fieldReferenceDescription.hiddenConditions) {
                const hiddenConditionForeignKeyPath = get(data, hiddenCondition.foreignKeyPath || '');
                if (isEqual(hiddenConditionForeignKeyPath, hiddenCondition.foreignKeyValueExpression)) {
                    fieldHasHiddenConditionMatched = true;
                    this.buttonState.hiddenReasons[hiddenCondition.reasonKey || ''] = true;
                    hiddenStatesChanged = true;
                } else {
                    delete this.buttonState.hiddenReasons[hiddenCondition.reasonKey || ''];
                    hiddenStatesChanged = true;
                }
            }
        }
        if (hiddenStatesChanged) {
            this.buttonState.hiddenReasons = {
                ...this.buttonState.hiddenReasons
            };
        }
        let disabledStateChanged = false;
        if (this.fieldReferenceDescription.disabledConditions) {
            for (const disabledCondition of this.fieldReferenceDescription.disabledConditions) {
                const valueAtForeignKeyPath = get(data, disabledCondition.foreignKeyPath || '');
                if (isEqual(valueAtForeignKeyPath, disabledCondition.foreignKeyValueExpression)) {
                    this.buttonState.disabledReasons[disabledCondition.reasonKey || ''] = true;
                    disabledStateChanged = true;
                } else {
                    delete this.buttonState.disabledReasons[disabledCondition.reasonKey || ''];
                    disabledStateChanged = true;
                }
            }
        }
        if (disabledStateChanged) {
            this.buttonState.disabledReasons = {
                ...this.buttonState.disabledReasons
            };
        }

        if (this.fieldReferenceDescription.hrefUpdateOption) {
            const newValue = get(data, this.fieldReferenceDescription.hrefForeignKeyPath || '');

            switch (this.fieldReferenceDescription.hrefUpdateOption) {
                case 'UPDATE_ALWAYS':
                    if (newValue !== undefined) {
                        this.buttonState.href = newValue;
                    }
                    break;
                case 'UPDATE':
                    if (newValue !== undefined) {
                        this.buttonState.href = newValue;
                    }
                    break;
                case 'UPDATE_IF_EMPTY':
                    if (!this.buttonState.href) {
                        this.buttonState.href = newValue;
                    }
                    break;
                default:
                    break;
            }
        }

        if (this.fieldReferenceDescription.textUpdateOption) {
            const newValue = get(data, this.fieldReferenceDescription.textForeignKeyPath || '');

            switch (this.fieldReferenceDescription.textUpdateOption) {
                case 'UPDATE_ALWAYS':
                    if (newValue !== undefined) {
                        this.buttonState.text = newValue;
                    }
                    break;
                case 'UPDATE':
                    if (newValue !== undefined) {
                        this.buttonState.text = newValue;
                    }
                    break;
                case 'UPDATE_IF_EMPTY':
                    if (!this.buttonState.text) {
                        this.buttonState.text = newValue;
                    }
                    break;
                default:
                    break;
            }
        }
        if (this.fieldReferenceDescription.iconUpdateOption) {
            const newValue = get(data, this.fieldReferenceDescription.iconForeignKeyPath || '');

            switch (this.fieldReferenceDescription.iconUpdateOption) {
                case 'UPDATE_ALWAYS':
                    if (newValue !== undefined) {
                        this.buttonState.icon = newValue;
                    }
                    break;
                case 'UPDATE':
                    if (newValue !== undefined) {
                        this.buttonState.icon = newValue;
                    }
                    break;
                case 'UPDATE_IF_EMPTY':
                    if (!this.buttonState.icon) {
                        this.buttonState.icon = newValue;
                    }
                    break;
                default:
                    break;
            }
        }
    }

    private onFieldEventTriggered(eventData?: AmetrasAdvancedButtonEvent) {
        if (eventData && eventData.buttonConfig) {
            Vue.set(eventData.buttonConfig, 'loading', true);
        }
        this.notifyFieldWatchOptionTriggeredFunction().finally(() => {
            if (eventData && eventData.buttonConfig) {
                Vue.set(eventData.buttonConfig, 'loading', false);
            }
        });
    }
}
